/**
 * Calendar subscription card — the user's private .ics feed of meetings
 * scheduled or booked in Kardoe OS, for Apple/Google/Outlook calendars.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { meetingFeed } from "../../lib/api";
import { Button, Input, toast, useConfirm } from "@/ui";

const FEED_KEY = ["me", "meeting-feed"] as const;

export default function MeetingFeedSettings() {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const feedQuery = useQuery({ queryKey: FEED_KEY, queryFn: () => meetingFeed.get() });
    const url = feedQuery.data?.ok ? feedQuery.data.url : null;

    const reset = useMutation({
        mutationFn: async () => {
            const res = await meetingFeed.reset();
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: FEED_KEY }),
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn't create the link"),
    });

    async function resetLink() {
        const ok = await confirm({
            title: "Reset calendar link?",
            description: "Calendars subscribed to the current link will stop updating.",
            confirmLabel: "Reset link",
        });
        if (ok) reset.mutate();
    }

    async function copy() {
        if (!url) return;
        await navigator.clipboard.writeText(url);
        toast.success("Link copied.");
    }

    return (
        <div className="settings-card">
            <h3>Subscribe in your calendar</h3>
            <p className="muted">
                A private link to meetings you schedule or that are booked with you, for Apple Calendar,
                Google Calendar, or Outlook. Anyone with the link can see these meetings. Google can take
                several hours to pick up changes.
            </p>
            {url ? (
                <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Input readOnly value={url} style={{ flex: 1, minWidth: 280 }}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.select()} />
                    <Button size="sm" onClick={copy}>Copy</Button>
                    <Button size="sm" onClick={() => { window.location.href = url.replace(/^https?:/, "webcal:"); }}>
                        Open in calendar app
                    </Button>
                    <Button size="sm" variant="danger" disabled={reset.isPending} onClick={resetLink}>
                        Reset link
                    </Button>
                </div>
            ) : (
                <Button disabled={feedQuery.isLoading || reset.isPending} onClick={() => reset.mutate()}>
                    {reset.isPending ? "Creating…" : "Create calendar link"}
                </Button>
            )}
        </div>
    );
}
